import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// User type definition
interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'doctor' | 'assistant';
  emailVerified?: boolean;
  phoneNumber?: string;
  department?: string;
  specialization?: string;
  profileImage?: string;
  permissions?: string[];
  attributes?: Record<string, any>;
  isActive?: boolean;
}

// Auth Context State
interface AuthContextState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (code: string, newPassword: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
  resendVerificationCode: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  checkRole: (role: string) => boolean;
  checkPermission: (permission: string) => boolean;
}

// Register data interface
interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'doctor' | 'assistant';
  phoneNumber?: string;
  department?: string;
  specialization?: string;
}

// Mock users database
const MOCK_USERS: Array<{
  id: string;
  email: string;
  password: string;
  name: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'doctor' | 'assistant';
  emailVerified: boolean;
  phoneNumber?: string;
  department?: string;
  specialization?: string;
}> = [
  {
    id: '1',
    email: 'admin@drsync.com',
    password: 'Admin123',
    name: 'Admin User',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    emailVerified: true,
    department: 'Yönetim',
  },
  {
    id: '2',
    email: 'doctor@drsync.com',
    password: 'Doctor123',
    name: 'Dr. Mehmet Öz',
    firstName: 'Mehmet',
    lastName: 'Öz',
    role: 'doctor',
    emailVerified: true,
    department: 'Kardiyoloji',
    specialization: 'Kardiyoloji Uzmanı',
  },
  {
    id: '3',
    email: 'assistant@drsync.com',
    password: 'Assistant123',
    name: 'Ayşe Demir',
    firstName: 'Ayşe',
    lastName: 'Demir',
    role: 'assistant',
    emailVerified: true,
    phoneNumber: '+90 555 123 4567',
    department: 'Resepsiyon',
  },
];

// Create the context
export const AuthContext = createContext<AuthContextState | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check both localStorage and sessionStorage
        const storedUser = localStorage.getItem('mockUser') || sessionStorage.getItem('mockUser');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean = false) => {
      setLoading(true);
      setError(null);

      try {
        // Call backend API for login
        const response = await api.post('/auth/login', { email, password });

        if (!response.success || !response.data) {
          throw new Error(response.message || 'Giriş başarısız');
        }

        const { user: userData, token } = response.data;

        // Store tokens
        api.setToken(token);
        if (response.data.refreshToken) {
          api.setRefreshToken(response.data.refreshToken);
        }

        // Store user data
        if (rememberMe) {
          localStorage.setItem('mockUser', JSON.stringify(userData));
        } else {
          sessionStorage.setItem('mockUser', JSON.stringify(userData));
        }

        setUser(userData);
        enqueueSnackbar('Giriş başarılı!', { variant: 'success' });

        // Navigate based on role
        switch (userData.role) {
          case 'admin':
            navigate('/admin');
            break;
          case 'doctor':
            navigate('/doctor');
            break;
          case 'assistant':
            navigate('/assistant');
            break;
          default:
            navigate('/');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Giriş başarısız';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate, enqueueSnackbar]
  );

  // Register function
  const register = useCallback(
    async (data: RegisterData) => {
      setLoading(true);
      setError(null);

      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if user already exists
        const exists = MOCK_USERS.find(u => u.email === data.email);
        if (exists) {
          throw new Error('Bu e-posta adresi zaten kayıtlı');
        }

        // Create new user
        const newUser: User = {
          id: Date.now().toString(),
          email: data.email,
          name: `${data.firstName} ${data.lastName}`,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          emailVerified: false,
          phoneNumber: data.phoneNumber,
          department: data.department,
          specialization: data.specialization,
        };

        // Add to mock database (in real app, this would be an API call)
        MOCK_USERS.push({
          id: newUser.id,
          email: newUser.email,
          password: data.password,
          name: newUser.name,
          firstName: data.firstName,
          lastName: data.lastName,
          role: newUser.role,
          emailVerified: newUser.emailVerified,
          phoneNumber: data.phoneNumber,
          department: data.department,
          specialization: data.specialization,
        });

        enqueueSnackbar('Kayıt başarılı! Giriş yapabilirsiniz.', { variant: 'success' });
        navigate('/login');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Kayıt başarısız';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate, enqueueSnackbar]
  );

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      // Clear storage
      localStorage.removeItem('mockUser');
      sessionStorage.removeItem('mockUser');
      api.removeTokens();

      setUser(null);
      enqueueSnackbar('Çıkış yapıldı', { variant: 'info' });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate, enqueueSnackbar]);

  // Forgot password function
  const forgotPassword = useCallback(
    async (email: string) => {
      setLoading(true);
      setError(null);

      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if user exists
        const user = MOCK_USERS.find(u => u.email === email);
        if (!user) {
          throw new Error('Bu e-posta adresi kayıtlı değil');
        }

        enqueueSnackbar('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi', {
          variant: 'success',
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'İşlem başarısız';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  // Reset password function
  const resetPassword = useCallback(
    async (code: string, newPassword: string) => {
      setLoading(true);
      setError(null);

      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        enqueueSnackbar('Şifreniz başarıyla güncellendi', { variant: 'success' });
        navigate('/login');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'İşlem başarısız';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate, enqueueSnackbar]
  );

  // Other mock functions
  const verifyEmail = useCallback(async (code: string) => {
    console.log('Verifying email with code:', code);
  }, []);

  const resendVerificationCode = useCallback(async () => {
    console.log('Resending verification code');
  }, []);

  const updateProfile = useCallback(async (data: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem('mockUser', JSON.stringify(updatedUser));
      enqueueSnackbar('Profil güncellendi', { variant: 'success' });
    }
  }, [user, enqueueSnackbar]);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    enqueueSnackbar('Şifre değiştirildi', { variant: 'success' });
  }, [enqueueSnackbar]);

  const refreshSession = useCallback(async () => {
    console.log('Refreshing session');
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const checkRole = useCallback((role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  }, [user]);

  const checkPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    // For mock, we'll give admin all permissions
    if (user.role === 'admin') return true;
    // Check if user has the specific permission
    return user.permissions?.includes(permission) || false;
  }, [user]);

  const value: AuthContextState = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationCode,
    updateProfile,
    changePassword,
    refreshSession,
    clearError,
    checkRole,
    checkPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextState => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};